import { Order } from '@@types/index';
import styled from '@emotion/styled';
import { Color } from '@resources/colors';
import { colFlex } from '@styles/flexStyles';
import { mobileMediaQuery } from '@styles/globalStyles';
import OrderModalFooterContents from './OrderModalFooterContents';
import OrderModalMainContents from './OrderModalMainContents';
import OrderModalHeaderContents from './OrderModalHeaderContents';
import { createPortal } from 'react-dom';
import { useEffect } from 'react';
import useModal from '@hooks/useModal';
import { orderModalReadOnlyAtom } from '@jotai/atoms';
import { useSetAtom } from 'jotai';

const DetailModalContainer = styled.div``;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(70, 74, 77, 0.3);
  backdrop-filter: blur(2px);
  z-index: 2000;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${Color.WHITE};
  border-radius: 10px;
  border: 1px solid #e8eef2;
  width: 500px;
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  z-index: 2001;
  gap: 12px;
  box-shadow: 0 4px 20px 0 rgba(92, 92, 92, 0.05);
  ${colFlex({ justify: 'center' })}

  ${mobileMediaQuery} {
    width: calc(100vw - 32px);
    max-height: calc(100vh - 32px);
  }
`;

interface OrderDetailModalProps {
  order: Order;
  isModalOpen: boolean;
  closeModal: () => void;
  readOnly?: boolean;
}

function OrderDetailModal({ order, isModalOpen, closeModal, readOnly = false }: OrderDetailModalProps) {
  const { modalKey } = useModal();

  const setReadOnly = useSetAtom(orderModalReadOnlyAtom);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    setReadOnly(readOnly);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
      setReadOnly(false);
    };
  }, [readOnly, setReadOnly]);

  if (!isModalOpen) {
    return null;
  }

  return createPortal(
    <DetailModalContainer>
      <ModalOverlay onClick={closeModal} />
      <ModalContainer>
        <OrderModalHeaderContents onClose={closeModal} order={order} />
        <OrderModalMainContents order={order} />
        <OrderModalFooterContents orderStatus={order.status} id={order.id} closeModal={closeModal} />
      </ModalContainer>
    </DetailModalContainer>,
    document.getElementById(modalKey) as HTMLElement,
  );
}

export default OrderDetailModal;
